import { Injectable } from '@angular/core';
import { IDocument, FileCategory, getFileCategory } from 'src/app/models/document.models';

export interface DocumentSearchResult {
  filtered: IDocument[];
  query: string;
  hasMore: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class DocumentSearchService {
  private readonly extensionMap: Record<string, string[]> = {
    word: ['doc', 'docx', 'docm'],
    excel: ['xls', 'xlt', 'xml', 'xlsx', 'xlsm', 'xlsb', 'xltx', 'xltm'],
    powerpoint: ['ppt', 'pptx', 'thmx', 'ppsx', 'pps', 'ppsm'],
    pdf: ['pdf'],
    image: ['png', 'jpg', 'jpeg'],
    media: ['mp4', 'wmv', '3gp', 'avi', 'mp3', 'wav'],
  };

  private readonly categoryKeywords: Record<string, string[]> = {
    word: ['word', 'doc', 'documento', 'texto'],
    excel: ['excel', 'xls', 'planilha', 'spreadsheet'],
    powerpoint: ['ppt', 'powerpoint', 'apresentacao', 'slides'],
    pdf: ['pdf'],
    png: ['png', 'imagem', 'image', 'picture', 'foto', 'photo'],
    jpg: ['jpg', 'jpeg', 'imagem', 'image', 'picture', 'foto', 'photo'],
    media: ['video', 'audio', 'media', 'mp4', 'mp3'],
  };

  filterDocuments(
    documents: IDocument[],
    query: string,
    language: string
  ): IDocument[] {
    if (!query || query.trim().length === 0) {
      return [...documents];
    }

    const lowered = query.toLowerCase().trim();
    const matchedExtensions = this.getExtensionsForQuery(lowered);

    return documents.filter((doc) => {
      const nameMatch =
        doc.name &&
        doc.name[language] &&
        doc.name[language].toLowerCase().includes(lowered);

      const extensionMatch =
        matchedExtensions.length > 0 &&
        matchedExtensions.includes(doc.type.toLowerCase());

      const categoryMatch = this.matchCategoryKeywords(lowered, doc.type);

      return !!nameMatch || extensionMatch || categoryMatch;
    });
  }

  getExtensionsForQuery(query: string): string[] {
    const lowered = query.toLowerCase();
    const results: string[] = [];

    for (const [key, extensions] of Object.entries(this.extensionMap)) {
      if (key.includes(lowered) || lowered.includes(key)) {
        results.push(...extensions);
      }
    }

    return results;
  }

  private matchCategoryKeywords(query: string, docType: string): boolean {
    const category = getFileCategory(docType);
    const categoryKey = category.toLowerCase().replace(' ', '_');

    for (const [key, keywords] of Object.entries(this.categoryKeywords)) {
      if (key === categoryKey || key === docType.toLowerCase()) {
        for (const kw of keywords) {
          if (query.includes(kw) || kw.includes(query)) {
            return true;
          }
        }
      }
    }

    return false;
  }

  highlightText(text: string, query: string): string {
    if (!query || query.trim().length === 0) return text;

    const lowered = query.toLowerCase().trim();
    const regex = new RegExp(`(${this.escapeRegex(lowered)})`, 'gi');
    return text.replace(regex, '<mark>$1</mark>');
  }

  private escapeRegex(str: string): string {
    return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }
}
